/* eslint-disable */
import { usd, pct, C } from "../ui/tokens";
import { base64ToUint8Array } from "./clientReports";

// ─── PROSPECT REPORT UTILITIES ───────────────────────────────────────────────
function drawFrontierScatter(frontierData, metric, returnRate) {
  const W = 680, H = 380;
  const PAD = { top: 32, right: 40, bottom: 58, left: 80 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#f8fafc"; ctx.fillRect(0, 0, W, H);
  if (!frontierData || !frontierData.points.length) {
    ctx.fillStyle = "#94a3b8"; ctx.font = "13px Arial"; ctx.textAlign = "center";
    ctx.fillText("Run Efficient Frontier to generate scatter", W / 2, H / 2);
    return canvas.toDataURL("image/png");
  }
  const YMAX = frontierData.yMax || 60;
  const getDelta = (pt) => metric === "value" ? pt.valueDelta : metric === "rmd" ? pt.rmdDelta : pt.wealthDelta;
  const pts = frontierData.points;
  const cxFn = v => PAD.left + (v / 100) * plotW;
  const cyFn = v => PAD.top + plotH - (v / YMAX) * plotH;
  ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = 0.5;
  for (let y = 0; y <= YMAX; y += 10) { ctx.beginPath(); ctx.moveTo(PAD.left, cyFn(y)); ctx.lineTo(PAD.left + plotW, cyFn(y)); ctx.stroke(); }
  for (let x = 0; x <= 100; x += 10) { ctx.beginPath(); ctx.moveTo(cxFn(x), PAD.top); ctx.lineTo(cxFn(x), PAD.top + plotH); ctx.stroke(); }
  const maxAbs = Math.max(...pts.map(p => Math.abs(getDelta(p))), 0.01);
  pts.forEach(pt => {
    const delta = getDelta(pt);
    const frac = Math.min(Math.abs(delta) / maxAbs, 1);
    const r = 2.5 + frac * 8.5;
    ctx.beginPath(); ctx.arc(cxFn(pt.x), cyFn(pt.y), r, 0, Math.PI * 2);
    if (delta >= 0) { ctx.fillStyle = `rgba(5,150,105,${0.18 + frac * 0.72})`; ctx.strokeStyle = `rgba(4,120,87,${0.3 + frac * 0.6})`; }
    else { ctx.fillStyle = `rgba(225,29,72,${0.18 + frac * 0.72})`; ctx.strokeStyle = `rgba(190,18,60,${0.3 + frac * 0.6})`; }
    ctx.lineWidth = 0.7; ctx.fill(); ctx.stroke();
  });
  ctx.fillStyle = "#64748b"; ctx.font = "9px Arial"; ctx.textAlign = "center";
  for (let x = 0; x <= 100; x += 10) ctx.fillText(x + "%", cxFn(x), H - PAD.bottom + 14);
  ctx.font = "10px Arial";
  ctx.fillText("SPIA Allocation (% of RMD)", PAD.left + plotW / 2, H - 6);
  ctx.textAlign = "right"; ctx.font = "9px Arial";
  for (let y = 0; y <= YMAX; y += 10) ctx.fillText(y + "%", PAD.left - 5, cyFn(y) + 3);
  ctx.save(); ctx.translate(12, PAD.top + plotH / 2); ctx.rotate(-Math.PI / 2);
  ctx.textAlign = "center"; ctx.font = "10px Arial"; ctx.fillStyle = "#64748b";
  ctx.fillText("Bond sleeve before purchase (% of IRA)", 0, 0); ctx.restore();
  ctx.fillStyle = "#1e3a5f"; ctx.font = "bold 11px Arial"; ctx.textAlign = "center";
  ctx.fillText(frontierData.fia ? `Efficient Frontier — SPIA Allocation × Bond Sleeve (FIA springing ${Math.round(frontierData.fia.allocationPct * 100)}%, ${frontierData.fia.term}-yr)` : "Efficient Frontier — SPIA Allocation × Bond Sleeve", W / 2, 14);
  return canvas.toDataURL("image/png");
}

function downloadProspectEML(client, result, iraAssets, returnRate, postSpiaReturnRate,
                              allocationPct, spiaTerm, spiaRate, arbitrageRate,
                              frontierData, frontierMetric, logoSrc) {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const clientName = `${client.first_name} ${client.last_name}`;
  const scatterDataUrl = frontierData ? drawFrontierScatter(frontierData, frontierMetric, returnRate) : null;

  const baseRows = result ? result.baseline.map(r => `
    <tr><td>${r.Year}</td><td>${r.Age}</td><td>${usd(r.IRAValue)}</td>
    <td>${r.Divisor.toFixed(1)}</td><td>${usd(r.RMD)}</td></tr>`).join("") : "";
  const spiaRows = result ? result.spiaProj.map(r => `
    <tr><td>${r.Year}</td><td>${r.Age}</td><td>${usd(r.IRAValue)}</td>
    <td style="color:#059669">${usd(r.SPIAIncome)}</td>
    <td>${usd(r.AdditionalRMD)}</td>
    <td style="color:#2563eb;font-weight:700">${usd(r.TotalSPIARMD)}</td></tr>`).join("") : "";

  const thS = `style="padding:6px 10px;background:#1e3a5f;color:#fff;font-size:11px;text-align:left;"`;
  const tdS = `style="padding:5px 10px;border:1px solid #e2e8f0;font-size:11px;"`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head>
<body style="font-family:Arial,sans-serif;color:#0f172a;max-width:720px;margin:0 auto;padding:24px;">
  <div style="text-align:center;margin-bottom:24px;border-bottom:2px solid #1e3a5f;padding-bottom:20px;">
    <img src="${logoSrc}" style="height:56px;object-fit:contain;" alt="Harness Financial"/>
    <h1 style="color:#1e3a5f;font-size:20px;margin:12px 0 2px;">Sec. 204 SPIA Analysis</h1>
    <div style="color:#64748b;font-size:13px;">Prepared for <b>${clientName}</b> &bull; ${today}</div>
    ${client.contact_address ? `<div style="color:#64748b;font-size:12px;margin-top:4px;">${client.contact_address}</div>` : ""}
  </div>
  <h2 style="color:#1e3a5f;font-size:14px;">Assumptions</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:20px;font-size:12px;">
    <tr><td style="padding:4px 8px;color:#64748b;">IRA Assets</td><td style="font-weight:700">${usd(iraAssets)}</td>
        <td style="padding:4px 8px;color:#64748b;">Baseline RoR</td><td style="font-weight:700">${pct(returnRate)}</td></tr>
    <tr><td style="padding:4px 8px;color:#64748b;">Post-SPIA RoR</td><td style="font-weight:700">${pct(postSpiaReturnRate)}</td>
        <td style="padding:4px 8px;color:#64748b;">SPIA Allocation</td><td style="font-weight:700">${pct(allocationPct)}</td></tr>
    <tr><td style="padding:4px 8px;color:#64748b;">SPIA Term</td><td style="font-weight:700">${spiaTerm} yr</td>
        <td style="padding:4px 8px;color:#64748b;">SPIA Premium</td><td style="font-weight:700">${result ? usd(result.premium) : "—"}</td></tr>
    <tr><td style="padding:4px 8px;color:#64748b;">FIA (springing)</td><td style="font-weight:700" colspan="3">${result?.fia ? `${usd(result.fia.amount)} (${Math.round((result.fia.allocationPct || 0) * 100)}% of deferred) — ${result.fia.term}-yr S&P 500 PI point-to-point, ${Math.round((result.fia.participation ?? 1) * 100)}% participation${result.fia.cap ? `, ${Math.round(result.fia.cap * 100)}% term cap` : ", uncapped"}${result.fia.rebalance ? " · rebalance at credit" : ""}${result.fia.terminateYear ? ` · terminates yr ${result.fia.terminateYear}` : ""}` : "None"}</td></tr>
  </table>
  <h2 style="color:#1e3a5f;font-size:14px;">Baseline Projection</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
    <tr><th ${thS}>Year</th><th ${thS}>Age</th><th ${thS}>IRA Value</th><th ${thS}>Divisor</th><th ${thS}>RMD</th></tr>
    ${baseRows || `<tr><td colspan="5" ${tdS}>Not calculated.</td></tr>`}
  </table>
  <h2 style="color:#1e3a5f;font-size:14px;">SPIA Scenario Projection</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
    <tr><th ${thS}>Year</th><th ${thS}>Age</th><th ${thS}>IRA Value</th><th ${thS}>SPIA Income</th><th ${thS}>Add'l RMD</th><th ${thS}>Total RMD</th></tr>
    ${spiaRows || `<tr><td colspan="6" ${tdS}>Not calculated.</td></tr>`}
  </table>
  ${scatterDataUrl ? `<h2 style="color:#1e3a5f;font-size:14px;">Efficient Frontier</h2>
  <img src="${scatterDataUrl}" style="width:100%;border-radius:8px;margin-bottom:16px;" alt="Efficient Frontier"/>` : ""}
  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;font-size:10px;color:#94a3b8;line-height:1.6;">
    This report is for educational and planning purposes only. Section 204 planning requires careful individual analysis considering longevity, morbidity, risk tolerance, fees, and the financial stability of the issuing annuity carrier. Consult a qualified tax and financial professional before deployment. All projections are based on assumed rates of return and are not guaranteed.
    Securities and advisory services offered through LPL Financial, Member FINRA/SIPC. Section 204 Plan™ is a trademark and patent pending of Harness Financial, LLC.
  </div>
</body></html>`;

  const eml = ["MIME-Version: 1.0", `Date: ${new Date().toUTCString()}`,
    "Subject: Sec. 204 SPIA Analysis", `To: ${client.contact_email || ""}`,
    'Content-Type: text/html; charset="utf-8"', "", html].join("\r\n");

  const blob = new Blob([eml], { type: "message/rfc822" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Sec204_Prospect_${client.last_name}_${client.first_name}.eml`;
  a.click();
}

async function downloadProspectDOCX(client, result, iraAssets, returnRate, postSpiaReturnRate,
                                     allocationPct, spiaTerm, spiaRate, arbitrageRate,
                                     frontierData, frontierMetric, logoSrc) {
  let mod;
  try { mod = await import("docx"); } catch { alert("docx package required"); return; }
  const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
          ImageRun, AlignmentType, WidthType, BorderStyle, ShadingType } = mod;

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const clientName = `${client.first_name} ${client.last_name}`;
  const logoB64 = logoSrc.includes(",") ? logoSrc.split(",")[1] : logoSrc;
  const logoBytes = base64ToUint8Array(logoB64);

  let scatterBytes = null;
  if (frontierData) {
    const dataUrl = drawFrontierScatter(frontierData, frontierMetric, returnRate);
    scatterBytes = base64ToUint8Array(dataUrl.split(",")[1]);
  }

  const border = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" };
  const borders = { top: border, bottom: border, left: border, right: border };
  const cellM = { top: 80, bottom: 80, left: 120, right: 120 };
  const hdrCell = (t, w) => new TableCell({ borders, width: { size: w, type: WidthType.DXA }, margins: cellM,
    shading: { fill: "1e3a5f", type: ShadingType.CLEAR },
    children: [new Paragraph({ children: [new TextRun({ text: t, bold: true, color: "FFFFFF", size: 20, font: "Arial" })] })] });
  const dataCell = (t, w, col) => new TableCell({ borders, width: { size: w, type: WidthType.DXA }, margins: cellM,
    children: [new Paragraph({ children: [new TextRun({ text: String(t ?? "—"), size: 20, font: "Arial", color: col || "0f172a" })] })] });
  const sp = (n=120) => new Paragraph({ spacing: { after: n }, children: [] });
  const ruled = new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "1e3a5f", space: 1 } }, children: [] });
  const h = (t, size=26) => new Paragraph({ spacing: { before: 240, after: 100 },
    children: [new TextRun({ text: t, bold: true, size, font: "Arial", color: "1e3a5f" })] });

  const baseRows = (result?.baseline || []).map(r => new TableRow({ children: [
    dataCell(r.Year, 1100), dataCell(r.Age, 800), dataCell(usd(r.IRAValue), 1800),
    dataCell(r.Divisor.toFixed(1), 900), dataCell(usd(r.RMD), 1600),
  ]}));
  const spiaRows = (result?.spiaProj || []).map(r => new TableRow({ children: [
    dataCell(r.Year, 1000), dataCell(r.Age, 800), dataCell(usd(r.IRAValue), 1500),
    dataCell(usd(r.SPIAIncome), 1400, "059669"), dataCell(usd(r.AdditionalRMD), 1400),
    dataCell(usd(r.TotalSPIARMD), 1460, "2563eb"),
  ]}));

  const docChildren = [
    new Paragraph({ alignment: AlignmentType.CENTER,
      children: [new ImageRun({ data: logoBytes, transformation: { width: 100, height: 77 }, type: "jpg" })] }),
    sp(80),
    new Paragraph({ alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: `Sec. 204 SPIA Analysis for ${clientName}`, bold: true, size: 32, font: "Arial", color: "1e3a5f" })] }),
    sp(40), ruled, sp(80),
    new Paragraph({ children: [new TextRun({ text: today, size: 22, font: "Arial", color: "475569" })] }),
    ...(client.contact_address ? client.contact_address.split("\n").map(line =>
      new Paragraph({ children: [new TextRun({ text: line, size: 22, font: "Arial", color: "475569" })] })
    ) : []),
    sp(160),
    h("Assumptions"),
    new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [2340, 2340, 2340, 2340],
      rows: [
        new TableRow({ children: [hdrCell("IRA Assets", 2340), dataCell(usd(iraAssets), 2340),
          hdrCell("Baseline RoR", 2340), dataCell(pct(returnRate), 2340)] }),
        new TableRow({ children: [hdrCell("Post-SPIA RoR", 2340), dataCell(pct(postSpiaReturnRate), 2340),
          hdrCell("SPIA Allocation", 2340), dataCell(pct(allocationPct), 2340)] }),
        new TableRow({ children: [hdrCell("SPIA Term", 2340), dataCell(`${spiaTerm} yr`, 2340),
          hdrCell("SPIA Premium", 2340), dataCell(result ? usd(result.premium) : "—", 2340)] }),
        new TableRow({ children: [hdrCell("FIA (springing)", 2340),
          dataCell(result?.fia ? `${usd(result.fia.amount)} (${Math.round((result.fia.allocationPct || 0) * 100)}% of deferred) — ${result.fia.term}-yr S&P 500 PI point-to-point, ${Math.round((result.fia.participation ?? 1) * 100)}% participation${result.fia.cap ? `, ${Math.round(result.fia.cap * 100)}% term cap` : ", uncapped"}${result.fia.rebalance ? " · rebalance at credit" : ""}${result.fia.terminateYear ? ` · terminates yr ${result.fia.terminateYear}` : ""}` : "None", 2340),
          hdrCell("", 2340), dataCell("", 2340)] }),
      ]
    }),
    sp(160),
    h("Baseline Projection"),
    ...(baseRows.length ? [new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [1100,800,1800,900,1600],
      rows: [new TableRow({ children: [hdrCell("Year",1100),hdrCell("Age",800),hdrCell("IRA Value",1800),hdrCell("Divisor",900),hdrCell("RMD",1600)] }), ...baseRows] }), sp(160)]
      : [new Paragraph({ children: [new TextRun({ text: "Not calculated.", size: 20, font: "Arial" })] }), sp(160)]),
    h("SPIA Scenario Projection"),
    ...(spiaRows.length ? [new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [1000,800,1500,1400,1400,1460],
      rows: [new TableRow({ children: [hdrCell("Year",1000),hdrCell("Age",800),hdrCell("IRA Value",1500),hdrCell("SPIA Income",1400),hdrCell("Add'l RMD",1400),hdrCell("Total RMD",1460)] }), ...spiaRows] }), sp(160)]
      : [new Paragraph({ children: [new TextRun({ text: "Not calculated.", size: 20, font: "Arial" })] }), sp(160)]),
    ...(scatterBytes ? [
      h("Efficient Frontier Map"),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 80, after: 80 },
        children: [new ImageRun({ data: scatterBytes, transformation: { width: 560, height: 315 }, type: "png" })] }),
      sp(120),
    ] : []),
    ruled, sp(80),
    new Paragraph({ children: [new TextRun({ text: "This report is for educational and planning purposes only. Section 204 planning requires careful individual analysis considering longevity, morbidity, risk tolerance, fees, and the financial stability of the issuing annuity carrier. All projections are based on assumed rates of return and are not guaranteed. Securities and advisory services offered through LPL Financial, Member FINRA/SIPC. Section 204 Plan™ is a trademark and patent pending of Harness Financial, LLC.", size: 16, font: "Arial", color: "94a3b8" })] }),
  ];

  const doc = new Document({ sections: [{ properties: { page: { size: { width: 12240, height: 15840 }, margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 } } }, children: docChildren }] });
  const blob = await Packer.toBlob(doc);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Sec204_Prospect_${client.last_name}_${client.first_name}.docx`;
  a.click();
}


export { drawFrontierScatter, downloadProspectEML, downloadProspectDOCX };
